<script setup lang="ts">
import AccountSidebar from '@/components/AccountSidebar/AccountSidebar.vue';
import { useI18n } from '@/i18n';
import { useSidebarStore } from '@/stores/sidebar';
import { Button, useAppBreakpoints } from '@dabble/quillui';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

const { t } = useI18n();
const { isMobile } = useAppBreakpoints();
const route = useRoute();
const { options } = storeToRefs(useSidebarStore());

const navOpen = ref(false);

const sectionTitle = computed(() => {
  const active = options.value.find((option) => option.selected);
  return t(active?.textKey ?? 'sidebar_title');
});

// Close the drawer on navigation and whenever we grow back to desktop.
watch(
  () => route.fullPath,
  () => (navOpen.value = false),
);
watch(isMobile, (mobile) => {
  if (!mobile) navOpen.value = false;
});
</script>

<template>
  <div class="account-layout">
    <header v-if="isMobile" class="account-layout__topbar">
      <Button
        icon-only="navigation-sidebar"
        variant="secondary"
        finish="ghost"
        :aria-label="t('sidebar_open_nav')"
        @click="navOpen = true"
      />
      <span class="account-layout__section">{{ sectionTitle }}</span>
    </header>

    <div
      v-if="isMobile && navOpen"
      class="account-layout__backdrop"
      :aria-label="t('sidebar_close_nav')"
      @click="navOpen = false"
    />

    <AccountSidebar
      class="account-layout__sidebar"
      :class="{ 'account-layout__sidebar--open': navOpen }"
      @navigate="navOpen = false"
    />

    <section class="account-layout__panel">
      <router-view v-slot="{ Component }">
        <component :is="Component" />
      </router-view>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.account-layout {
  // Equal 1fr flanks keep the centre column (the card) truly viewport-centred.
  // The sidebar rides the left flank; the empty right flank is the spacer
  // that collapses first as the screen narrows, since the left flank can't
  // shrink below the sidebar's width.
  display: grid;
  grid-template-columns: 1fr minmax(0, 720px) 1fr;
  align-items: start;
  gap: space('medium');
  min-height: 100vh;
  min-height: 100dvh;
  padding: space('small');
  box-sizing: border-box;
  // The login page's paper-collage sky, so accounts feels like one product
  // with the auth pages instead of a grey admin shell.
  background: url('/images/auth-background.png');
  background-position: center bottom;
  background-size: cover;
  background-attachment: fixed;
  box-shadow: inset 0 0 100px 10px rgba(#000, 0.3);

  @include media-query-below('tablet') {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 0;
    background-attachment: scroll;
  }

  &__topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: space('xsmall');
    padding: space('xsmall') space('small');

    // Keep the section title truly centred regardless of the button's width.
    // (The bar is `position: sticky`, so it anchors the absolute button.)
    > :first-child {
      position: absolute;
      left: space('xxsmall');
      top: 50%;
      transform: translateY(-50%);
    }
    background: theme('color-glass-bg');
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
    border-bottom: 1px solid theme('color-glass-edge');
    position: sticky;
    top: 0;
    z-index: layer('overlap');
  }

  &__section {
    font-weight: fontWeight('semibold');
    color: theme('color-text-primary');
  }

  &__backdrop {
    position: fixed;
    inset: 0;
    z-index: layer('drawer');
    background: theme('color-bg-modal-overlay');
  }

  &__sidebar {
    position: sticky;
    top: space('small');
    justify-self: end;
    max-height: calc(100vh - #{space('small')} * 2);
    overflow-y: auto;

    @include media-query-below('tablet') {
      position: fixed;
      top: space('xxsmall');
      left: space('xxsmall');
      bottom: space('xxsmall');
      max-height: none;
      z-index: layer('popover');
      transform: translateX(calc(-100% - #{space('xxsmall')}));
      transition: transform 0.2s ease;

      @media (prefers-reduced-motion: reduce) {
        transition: none;
      }
    }
  }

  &__sidebar--open {
    @include media-query-below('tablet') {
      transform: translateX(0);
    }
  }

  &__panel {
    position: relative;
    width: 100%;
    min-height: min(880px, calc(100vh - #{space('small')} * 2));
    min-height: min(880px, calc(100dvh - #{space('small')} * 2));
    background: theme('color-fg-app');
    border-radius: borderRadius('medium');
    // Matches AppBrandBlock, the card this layout echoes from the login page.
    box-shadow: 0 4px 32px 0 rgba(0, 0, 0, 0.2);
    padding: space('xlarge') space('xxlarge') space('xxlarge');
    box-sizing: border-box;
    overflow: hidden;

    // The login card's tri-color brand ribbon.
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 6px;
      background: linear-gradient(
        to right,
        theme('color-brand-gold') 0 33.34%,
        theme('color-brand-red') 33.34% 66.67%,
        theme('color-brand-teal') 66.67% 100%
      );
    }

    @include media-query-below('tablet') {
      width: auto;
      margin: space('xxsmall');
      min-height: 0;
      padding: space('medium') space('small');
    }
  }
}
</style>
